import type { DateRangeKey, UserActivityFilter } from "./types";

export const DATE_RANGE_OPTIONS: { label: string; value: DateRangeKey }[] = [
  { label: "Last Day", value: "1d" },
  { label: "Last Week", value: "1w" },
  { label: "Last Month", value: "1m" },
  { label: "Last Year", value: "1y" },
  { label: "Custom Range", value: "custom" },
];

export const DEFAULT_DATE_RANGE: DateRangeKey = "1m";

export const MAX_CUSTOM_RANGE_DAYS = 365;

export const USAGE_TRACKING_START = new Date(2026, 3, 1);

export const USER_ACTIVITY_ACTIVE: UserActivityFilter = "ACTIVE";
export const USER_ACTIVITY_ALL: UserActivityFilter = "ALL";
export const USER_ACTIVITY_NON_ACTIVE: UserActivityFilter = "NON_ACTIVE";

export const USER_ACTIVITY_OPTIONS: {
  label: string;
  value: UserActivityFilter;
}[] = [
  { label: "Active Users", value: USER_ACTIVITY_ACTIVE },
  { label: "All Users", value: USER_ACTIVITY_ALL },
  { label: "Non-Active Users", value: USER_ACTIVITY_NON_ACTIVE },
];

export const USER_ACTIVITY_EMPTY_MESSAGE: Record<UserActivityFilter, string> = {
  [USER_ACTIVITY_ACTIVE]: "No usage data found for the selected period.",
  [USER_ACTIVITY_ALL]: "No users found for this organization.",
  [USER_ACTIVITY_NON_ACTIVE]: "No non-active users for the selected filters.",
};

export const SOLUTION_KM = "KNOWLEDGE_MANAGEMENT";
export const SOLUTION_RA = "REPORT_AUTOMATION";
export const SOLUTION_MI = "MARKET_INTELLIGENCE";
export const SOLUTION_AD = "AUTO_DD";

export const API_ROUTES = {
  config: "/usage/config",
  summary: "/usage/summary",
  trends: "/usage/trends",
  data: "/usage/data",
  userReports: "/usage/user-reports",
  userAgents: "/usage/user-agents",
  dataExport: "/usage/data/export",
  budget: "/usage/budget",
  overviewSummary: "/usage/overview/summary",
  overviewData: "/usage/overview/data",
  organizationUsers: (orgId: number) => `/organizations/${orgId}/users`,
} as const;

export const QUERY_KEYS = {
  config: () => ["usage-config"] as const,
  summary: (
    solution: string,
    startDate: number,
    endDate: number,
    orgIds?: number[],
    userIds?: string[],
    types?: string[],
  ) =>
    [
      "usage-summary",
      solution,
      startDate,
      endDate,
      orgIds,
      userIds,
      types,
    ] as const,
  trends: (
    solution: string,
    startDate: number,
    endDate: number,
    orgIds?: number[],
    userIds?: string[],
    types?: string[],
  ) =>
    [
      "usage-trends",
      solution,
      startDate,
      endDate,
      orgIds,
      userIds,
      types,
    ] as const,
  data: (
    solution: string,
    startDate: number,
    endDate: number,
    page: number,
    orgIds?: number[],
    userIds?: string[],
    types?: string[],
    sortBy?: string,
    userActivity?: UserActivityFilter,
  ) =>
    [
      "usage-data",
      solution,
      startDate,
      endDate,
      page,
      orgIds,
      userIds,
      types,
      sortBy,
      userActivity,
    ] as const,
  typeBreakdown: (
    solution: string,
    startDate: number,
    endDate: number,
    orgIds?: number[],
    userIds?: string[],
    types?: string[],
  ) =>
    [
      "usage-type-breakdown",
      solution,
      startDate,
      endDate,
      orgIds,
      userIds,
      types,
    ] as const,
  userReports: (
    userId: string,
    startDate: number,
    endDate: number,
    page: number,
    orgIds?: number[],
    types?: string[],
  ) =>
    [
      "usage-user-reports",
      userId,
      startDate,
      endDate,
      page,
      orgIds,
      types,
    ] as const,
  userAgents: (
    userId: string,
    startDate: number,
    endDate: number,
    page: number,
    orgIds?: number[],
    types?: string[],
  ) =>
    [
      "usage-user-agents",
      userId,
      startDate,
      endDate,
      page,
      orgIds,
      types,
    ] as const,
  budget: (orgId?: number) => ["usage-budget", orgId] as const,
  overviewSummary: (
    startDate: number,
    endDate: number,
    orgIds?: number[],
    userIds?: string[],
    solution?: string,
  ) =>
    [
      "overview-summary",
      startDate,
      endDate,
      orgIds,
      userIds,
      solution,
    ] as const,
  overviewData: (
    startDate: number,
    endDate: number,
    page: number,
    orgIds?: number[],
    userIds?: string[],
    solution?: string,
    sortBy?: string,
    userActivity?: UserActivityFilter,
  ) =>
    [
      "overview-data",
      startDate,
      endDate,
      page,
      orgIds,
      userIds,
      solution,
      sortBy,
      userActivity,
    ] as const,
  organizationUsers: (orgId: number) => ["organization-users", orgId] as const,
} as const;

export const PAGE_SIZE = 8;

export const OVERVIEW_PAGE_SIZE = 10;
export const OVERVIEW_DEFAULT_SORT = "last_login:desc";
